/**
 * ConvoyDetailSection — Convoy-specific load detail panel (column 1 of expanded row).
 *
 * Layout-twinned with `EquipmentDetailsSection`: NO outer card / border / background —
 * renders directly on the row zebra. Header is a gray pill ("Load Detail"); rows are
 * label-on-left / value-on-right with a copy icon on the Convoy ID (matching the
 * "Load ID" copy affordance carriers use across other loadboards).
 *
 * Fields are deliberately curated:
 *   - Distance / Weight / Payout are ALREADY in the table row above the expansion —
 *     duplicating them here just clutters the panel, so they're omitted.
 *   - Stops live in column 2 (Trip view) — that's where pickup/dropoff lookup belongs.
 *   - We keep: Quickpay, Convoy ID (with copy), Shipper, Trailer, Accessorial, Notes, Broker.
 *
 * Vertical scrollable so a long accessorial list / notes block doesn't push the row
 * taller than the fixed expanded-row height.
 *
 * `detail` is fetched once in `LoadExpandedRow` via `useConvoyOfferDetail` and shared
 * with TripScheduleSection — no double network call.
 */

import React, { useCallback, useState } from 'react'
import { Tooltip } from '@mui/material'
import { cn } from 'utils/cn'
import { ConvoyOfferDetail } from '../../api/convoy.api'

interface ConvoyDetailSectionProps {
    detail: ConvoyOfferDetail | null
    loading: boolean
    error: string | null
}

const CopyIcon: React.FC<{ copied: boolean }> = ({ copied }) =>
    copied ? (
        <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-[var(--lh-success)]"
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
    )

function formatTrailer(detail: ConvoyOfferDetail): string {
    const types = detail.shipment?.requestedTruckTypes ?? []
    const labelMap: Record<string, string> = {
        DRY_VAN: 'Dry Van',
        REEFER: 'Reefer',
        FLATBED: 'Flatbed',
        POWER_ONLY: 'Power Only',
    }
    const labels = types.map((t) => labelMap[t] || t)
    return labels.length > 0 ? labels.join(' / ') : '—'
}

function formatAccessorials(arr: string[] | undefined): string {
    if (!arr || arr.length === 0) return '—'
    return arr
        .map((a) =>
            a
                .split('_')
                .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
                .join(' ')
        )
        .join(', ')
}

function humanize(value: string | null | undefined): string {
    if (!value) return ''
    return value
        .split('_')
        .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
        .join(' ')
}

export const ConvoyDetailSection: React.FC<ConvoyDetailSectionProps> = ({ detail, loading, error }) => {
    const [copiedField, setCopiedField] = useState<string | null>(null)

    const handleCopy = useCallback(async (text: string, field: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopiedField(field)
            setTimeout(() => setCopiedField(null), 1500)
        } catch {
            // silently fail — clipboard can be denied in iframes / non-secure contexts
        }
    }, [])

    if (loading && !detail) {
        return <div className="w-full h-full px-2 py-3 text-[12px] text-[var(--lh-fg-muted)] animate-pulse">Loading Convoy details…</div>
    }

    if (error && !detail) {
        return <div className="w-full h-full px-2 py-3 text-[12px] text-[var(--lh-danger)]">{error}</div>
    }

    if (!detail || !detail.shipment) return null

    const shipment = detail.shipment
    const fastestPay = shipment.broker?.fastestCarrierPaymentSpeed
    const brokerName = shipment.broker?.name

    // Curated rows — Distance/Weight/Payout deliberately omitted (visible in the parent table row).
    type Row = { label: string; value: string; copy?: boolean; multiline?: boolean }
    const rows: Row[] = []
    if (fastestPay) {
        rows.push({
            label: 'Quickpay',
            value: `${brokerName || 'Broker'} offers ${humanize(fastestPay).toLowerCase()} payouts.`,
            multiline: true,
        })
    }
    if (detail.convoyId) rows.push({ label: 'Convoy ID', value: detail.convoyId, copy: true })
    if (shipment.shipper?.name) rows.push({ label: 'Shipper', value: shipment.shipper.name })
    rows.push({ label: 'Trailer', value: formatTrailer(detail) })
    if (shipment.accessorials && shipment.accessorials.length > 0) {
        rows.push({ label: 'Accessorial', value: formatAccessorials(shipment.accessorials), multiline: true })
    }
    if (shipment.loadDescription) rows.push({ label: 'Notes', value: shipment.loadDescription, multiline: true })
    // Broker name + MC moved to col 3 (InformationSection) so they sit next to the
    // factoring approval chip; we still surface the Quickpay line here which uses brokerName.

    return (
        // Transparent — no border, no card. Mirrors EquipmentDetailsSection so Convoy
        // and non-Convoy expansions share the same visual silhouette in column 1.
        <div className="w-full h-full min-w-0 flex flex-col gap-1 overflow-y-auto">
            {/* Gray pill header — matches "Equipment details" styling */}
            <div className="h-7 min-h-[28px] flex items-center px-2 rounded-lg bg-[var(--lh-surface-secondary)] text-[12px] font-medium text-[var(--lh-fg)] mb-1 shrink-0">
                Load Detail
            </div>

            {rows.map((r) => (
                <div
                    key={r.label}
                    className={cn(
                        'flex items-center py-[3px] text-[12px] gap-2',
                        // Long-text rows (notes, accessorials, quickpay) wrap to multi-line
                        // and benefit from top alignment so the label stays anchored at the
                        // first line of the value.
                        r.multiline ? 'items-start' : 'items-center',
                        'justify-between'
                    )}
                >
                    <span className={cn('text-[var(--lh-fg-muted)] shrink-0', r.multiline && 'pt-0.5')}>{r.label}</span>
                    <span className={cn('flex items-center gap-1.5 min-w-0', r.multiline ? 'items-start text-right' : '')}>
                        <span
                            className={cn(
                                'font-semibold text-[var(--lh-fg)] break-words',
                                r.copy && 'cursor-pointer hover:opacity-80',
                                r.multiline && 'text-right whitespace-pre-line'
                            )}
                            onClick={r.copy ? () => handleCopy(r.value, r.label) : undefined}
                        >
                            {r.value}
                        </span>
                        {r.copy && (
                            <Tooltip title={copiedField === r.label ? 'Copied!' : 'Copy'} arrow placement="top">
                                <button
                                    onClick={() => handleCopy(r.value, r.label)}
                                    className="w-5 h-5 flex items-center justify-center border-none bg-transparent cursor-pointer text-[var(--lh-fg-muted)] hover:text-[var(--lh-fg)] transition-colors shrink-0"
                                >
                                    <CopyIcon copied={copiedField === r.label} />
                                </button>
                            </Tooltip>
                        )}
                    </span>
                </div>
            ))}
        </div>
    )
}
